/* eslint-disable import/exports-last */
import classNames from 'classnames'
import selectStyles from '../Select/Select.module.css'
import styles from './Dropdown.module.css'
import wrapperStyles from '../TextInputWrapper/TextInputWrapper.module.css'
import {
  Alignment,
  Popup,
  type PopupContext,
  Position,
  type TriggerEvents,
  findFirstFocusableChildOrRoot,
  findFocusableChildren,
  usePopupContext,
} from '../Popup'
import { type CSSProperties, type ReactNode, useCallback, useEffect, useRef } from 'react'
import { DropdownProps } from './types'
import { Icon } from '../Icon'
import { Keys, type MaybeHtml } from '../Popup/Popup'
import {
  findFirstFocusableChild,
  findNextFocusableElementExcludingChildren,
  findNextFocusableSibling,
  findPreviousFocusableSibling,
  tick,
} from '../Popup'
const triggerEvents: TriggerEvents = ['click', 'keypress']

export function DropdownTrigger({
  children,
  style,
  className,
  id,
}: {
  children?: ReactNode
  style?: CSSProperties
  className?: string
  id?: string
}) {
  return (
    <button
      id={id}
      style={style}
      className={classNames(selectStyles.selectContainer, selectStyles.select, styles.dropdownTriggerButton, className)}
      type="button"
    >
      {children}
      <Icon name="chevronDown" />
    </button>
  )
}

export function DropdownLabel({
  children,
  className,
  required,
  hint,
  id,
}: {
  children?: ReactNode
  hint?: ReactNode
  className?: string
  required?: boolean
  id?: string
}) {
  return (
    <div>
      {children ? (
        <label htmlFor={id} className={classNames(wrapperStyles.label, className)}>
          <div>
            {children}
            {required && <span aria-label="(required)">*</span>}
          </div>
          {hint ? <div className={classNames(wrapperStyles.hint)}>{hint}</div> : null}
        </label>
      ) : null}
    </div>
  )
}

export function DropdownMenu({ children, className }: { children?: ReactNode; className?: string }) {
  return <ul className={classNames(styles.dropdownMenu, className)}>{children}</ul>
}

export function DropdownMenuItem({
  children,
  style,
  className,
}: {
  children?: ReactNode
  style?: CSSProperties
  className?: string
}) {
  const ref = useRef<HTMLLIElement>(null)
  const { preferredPopupPosition, hidePopup, parentContext, spyRef } = useDropdownContext()
  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      const position = parentContext && preferredPopupPosition
      const closePopupKey = position && PositionToArrowKey[position].close
      if (event.key === Keys.Down || event.key === Keys.Up) {
        event.preventDefault()
        const siblingToFocus =
          event.key === Keys.Up ? findPreviousFocusableSibling(ref.current) : findNextFocusableSibling(ref.current)
        siblingToFocus?.focus()
        return
      }
      if (event.key === closePopupKey) {
        event.preventDefault()
        findFirstFocusableChildOrRoot(spyRef?.current?.nextElementSibling)?.focus()
        hidePopup()
      }
      // Something weird is happening with Enter behaviour, adding a workaround for now
      if (event.key === Keys.Enter && event.target instanceof HTMLElement && 'click' in event.target) {
        event.target.click()
      }
    },
    [hidePopup, parentContext, preferredPopupPosition, spyRef]
  )
  useEffect(() => {
    const ele = findFirstFocusableChild(ref.current)
    if (!ele) return
    ele.addEventListener('keydown', onKeyDown)
    ele.addEventListener('click', hidePopup)
    return () => {
      ele.removeEventListener('keydown', onKeyDown)
      ele.removeEventListener('click', hidePopup)
    }
  }, [hidePopup, onKeyDown])
  return (
    <li ref={ref} style={style} className={classNames(styles.dropdownMenuItem, className)}>
      {children}
    </li>
  )
}

export function DropdownMenuSeparator() {
  return <li className={styles.dropdownMenuSeparator}></li>
}

const PositionToArrowKey = {
  [Position.BOTTOM]: { open: Keys.Down, close: Keys.Down },
  [Position.TOP]: { open: Keys.Up, close: Keys.Down },
  [Position.RIGHT]: { open: Keys.Right, close: Keys.Left },
  [Position.LEFT]: { open: Keys.Left, close: Keys.Right },
} as const

function DropdownTriggerEventHandler({ children }: { children?: ReactNode }) {
  const { spyRef, popupRef, showPopup, preferredPopupPosition } = useDropdownContext()
  // we extend the popup functionality with select-like behaviour
  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      const position = preferredPopupPosition ?? Position.BOTTOM
      const openPopupKey = PositionToArrowKey[position].open
      if (event.key !== openPopupKey) return
      event.preventDefault()
      showPopup()
      // tick to let changes propagate, see the tick function for a better explaination
      void tick().then(() => {
        let focusable = findFirstFocusableChild(popupRef?.current)
        if (focusable) {
          focusable.focus()
          return
        }
        if (event.key !== Keys.Tab) return
        focusable = findNextFocusableElementExcludingChildren(spyRef?.current?.nextElementSibling)
        focusable?.focus()
      })
    },
    [popupRef, preferredPopupPosition, showPopup, spyRef]
  )
  useEffect(() => {
    const ele = spyRef?.current?.nextElementSibling as MaybeHtml
    if (!ele) return
    ele.addEventListener('keydown', onKeyDown)
    return () => ele.removeEventListener('keydown', onKeyDown)
  }, [onKeyDown, spyRef])

  return children
}

function DropdownContentEventHandler({ children }: { children?: ReactNode }) {
  const { spyRef, popupRef, hidePopup, open } = useDropdownContext()

  const handleFirstElement = useCallback(
    (event: KeyboardEvent) => {
      if (event.key !== Keys.Tab || !event.shiftKey) return
      event.preventDefault()
      findFirstFocusableChildOrRoot(spyRef?.current?.nextElementSibling)?.focus()
      hidePopup()
    },
    [spyRef, hidePopup]
  )
  // it needs to find the natural next focusable item of the trigger and focus it
  const handleLastElement = useCallback(
    (event: KeyboardEvent) => {
      if (event.key !== Keys.Tab || event.shiftKey) return
      event.preventDefault()
      const focusable = findNextFocusableElementExcludingChildren(spyRef?.current?.nextElementSibling)
      focusable?.focus()
    },
    [spyRef]
  )

  useEffect(() => {
    const ele = popupRef?.current as MaybeHtml
    if (!ele) return
    const elements = findFocusableChildren(ele)
    if (!elements || !elements.length) return
    const first = elements.item(0)
    const last = elements.item(elements.length - 1)
    first.addEventListener('keydown', handleFirstElement)
    last.addEventListener('keydown', handleLastElement)
    return () => {
      first.removeEventListener('keydown', handleFirstElement)
      last.removeEventListener('keydown', handleLastElement)
    }
  }, [handleFirstElement, handleLastElement, spyRef, popupRef, children, open])
  return children
}

function Dropdown({
  trigger,
  children,
  className,
  alignContent = Alignment.JUSTIFY,
  wrapperClassName,
  anchorClassName,
  preferredPopupPosition: position,
  marginInPx,
  Anchor,
}: DropdownProps) {
  const { parentContext } = useDropdownContext()
  const preferredPopupPosition =
    position ?? (parentContext ? (parentContext.preferredPopupPosition ?? Position.RIGHT) : Position.BOTTOM)
  return (
    <Popup
      content={<DropdownContentEventHandler>{children}</DropdownContentEventHandler>}
      contentClassName={classNames(styles.dropdownContent, className)}
      contentWrapperClassName={classNames(styles.scaleTransition, wrapperClassName)}
      popupOpenWrapperClassName={styles.scaleTransitionOpen}
      triggerEvents={triggerEvents}
      preferredPopupPosition={preferredPopupPosition}
      alignPopup={alignContent}
      anchorClassName={anchorClassName}
      Anchor={Anchor}
      marginInPx={marginInPx}
    >
      <DropdownTriggerEventHandler>{trigger}</DropdownTriggerEventHandler>
    </Popup>
  )
}

type DropDownContext = Pick<
  PopupContext,
  'showPopup' | 'hidePopup' | 'open' | 'togglePopup' | 'spyRef' | 'popupRef' | 'alignPopup' | 'preferredPopupPosition'
> & { parentContext?: DropDownContext }

export function useDropdownContext(): DropDownContext {
  const {
    showPopup,
    hidePopup,
    togglePopup,
    spyRef,
    popupRef,
    inDOM: open,
    parentContext: ctx,
    alignPopup,
    preferredPopupPosition,
  } = usePopupContext()
  const parentContext = ctx && ctx.initialised ? (ctx as DropDownContext) : undefined
  return {
    open,
    showPopup,
    hidePopup,
    togglePopup,
    spyRef,
    popupRef,
    parentContext,
    alignPopup,
    preferredPopupPosition,
  } as const
}

export default Dropdown
